import { useState, useEffect, useCallback } from "react";

// Custom hook to check PWA installation and storage persistence status
export const usePwaStatus = () => {
  // State for PWA installation status
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // State for storage persistence status
  const [persistence, setPersistence] = useState({
    persisted: false,
    status: "Initializing...",
  });

  // Function to check the current persistence state without requesting it
  const checkPersistence = useCallback(async () => {
    if (!navigator.storage || !navigator.storage.persisted) {
      setPersistence({ persisted: false, status: "Persistent storage not supported." });
      return;
    }
    try {
      const isPersisted = await navigator.storage.persisted();
      setPersistence({
        persisted: isPersisted,
        status: isPersisted ? "Storage is persistent." : "Storage is not persistent.",
      });
    } catch (error) {
      console.error("Error checking persistence:", error);
      setPersistence({ persisted: false, status: "Error checking persistence." });
    }
  }, []);

  // Effect to run checks on component mount
  useEffect(() => {
    // Method 1: The 'display-mode' media query is the modern, standard way to check.
    // It's true if the app was launched from its icon.
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

    // Method 2: A fallback for older iOS Safari versions.
    // `window.navigator.standalone` is a non-standard property.
    const isNavigatorStandalone = window.navigator.standalone === true;

    // If either condition is true, the app is considered installed.
    if (isStandalone || isNavigatorStandalone) {
      setIsPwaInstalled(true);
    }
  }, []); // Rerun if the check function changes (it won't, due to useCallback)

  // On component mount, run the initial check.
  useEffect(() => {
    checkPersistence();
  }, [checkPersistence]);

  // Function to request persistent storage
  const requestPersistence = useCallback(async () => {
    if (!navigator.storage || !navigator.storage.persist) {
      setPersistence({ persisted: false, status: "Persistent storage not supported." });
      return;
    }
    try {
      const isPersisted = await navigator.storage.persisted();

      if (!isPersisted) {
        setPersistence((prev) => ({ ...prev, status: "Requesting permission..." }));
        const granted = await navigator.storage.persist();
        setPersistence({
          persisted: granted,
          status: granted
            ? "Persistent storage granted."
            : "Persistent storage not granted.",
        });
      }
    } catch (error) {
      console.error("Error requesting persistence:", error);
      setPersistence({ persisted: false, status: "Error requesting permission." });
    }
  }, []);

  // Expose all relevant state and actions
  return { isPwaInstalled, persistence, requestPersistence };
};
